import { reduceTokenString } from '@/common/utils/app.utils';
import { getBoardId } from '@/core/web-storage/webStorage.utils';
import { formatDateWithHyphens } from '@/common/utils/date.utils';
import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import { getFirstErrorOrDefault } from '@/core/network/network.utils';
import tokens from '@/contract/tokens.json';

export type ContractResponseItemShape = {
  contractId: number;
  multipleContractId: string;
  description: string;
  effectiveDate: string;
  expiryDate: string | null;
  active: boolean;
  expired: boolean;
  editable: boolean;
};

type ErrorResponseItemShape = {
  exceptionMessage: string;
  context: string;
};

type GetContractsResponse = {
  error: ErrorResponseItemShape[] | [];
  data: ContractResponseItemShape[];
};

type CreateContractResponse = {
  data: {
    contractId: number;
  };
  errors: ErrorResponseItemShape[] | [];
};

type MutateExistingContractResponseType = {
  data: string;
  errors: ErrorResponseItemShape[] | [];
};

type UpdateContractRequestType = {
  contractId: number;
  contract: ContractData;
};

type DeleteContractRequestType = {
  contractId: number;
};

type ContractData = {
  multipleContractId: string;
  description: string;
  effectiveDate: string;
  expiryDate: string | null;
};

const contractsApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getContracts: build.query<ContractResponseItemShape[], { status?: string }>({
      query(params) {
        const boardId = getBoardId();
        const { status } = params || {};
        const filter = status ? `?filter=${status}` : '';
        return {
          url: `v1/employers/${boardId}/contracts${filter}`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: GetContractsResponse) {
        const inner = response?.data.map((contractData) => ({
          ...contractData,
          effectiveDate: formatDateWithHyphens(contractData.effectiveDate),
          expiryDate: contractData.expiryDate
            ? formatDateWithHyphens(contractData.expiryDate)
            : null,
        }));
        return inner;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR_FETCHING'], 'contracts'),
        );
      },
      providesTags: ['CONTRACTS'],
    }),
    createContract: build.mutation<CreateContractResponse, ContractData>({
      query(contractData) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/contracts`,
          method: 'POST',
          body: { ...contractData },
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'creating new'),
        );
      },
      invalidatesTags: ['CONTRACTS'],
    }),
    updateContract: build.mutation<MutateExistingContractResponseType, UpdateContractRequestType>({
      query({ contractId, contract }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/contracts/${contractId}`,
          method: 'POST',
          body: {
            ...contract,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'updating'),
        );
      },
      invalidatesTags: ['CONTRACTS'],
    }),
    deleteContract: build.mutation<MutateExistingContractResponseType, DeleteContractRequestType>({
      query({ contractId }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/contracts/${contractId}`,
          method: 'DELETE',
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'deleting'),
        );
      },
      invalidatesTags: ['CONTRACTS'],
    }),
  }),
  overrideExisting: false,
});

export { contractsApi };
